import { BiometricAuth } from '@aparajita/capacitor-biometric-auth';

import { isStoreBuild } from './platform';

/**
 * Вход по лицу или отпечатку.
 *
 * ЧЕМ ЭТО ЯВЛЯЕТСЯ И ЧЕМ НЕ ЯВЛЯЕТСЯ. Биометрия здесь — не «вместо пароля», а
 * ворота перед сессией, которая уже лежит в защищённом хранилище платформы
 * (см. `lib/secureStorage`). Само шифрование обеспечивает система, биометрия
 * добавляет проверку, что телефон в руках владельца.
 *
 * Полный вариант — когда ключ шифрования лежит в защищённом сопроцессоре и без
 * лица не расшифровывается вовсе — требует нативного кода и в нашей связке
 * плагинов недоступен. Разница честная: при нашем варианте злоумышленник с
 * разблокированным телефоном теоретически мог бы добраться до Keychain, при
 * полном — нет. Для учебного приложения этого уровня достаточно, но записываем
 * как есть, чтобы через полгода никто не решил, что защита сильнее, чем она есть.
 *
 * ЗАЧЕМ ВООБЩЕ. Токен обновления живёт неделю, и без биометрии продлевать его
 * надолго нельзя: украденный телефон означал бы доступ к аккаунту. С проверкой
 * личности срок можно ставить в разы больше, и человек почти не видит экран
 * входа — при этом защита выше, чем была.
 */

const PREF_KEY = 'onebilim:biometrics';
/** Спрашивали ли уже. Отдельно от самой настройки: «нет» — тоже ответ. */
const ASKED_KEY = 'onebilim:biometrics-asked';

export type BiometryInfo = {
  available: boolean;
  /** «Face ID», «Touch ID», «Отпечаток» — как назвать в интерфейсе. */
  name: string;
};

export async function checkBiometry(): Promise<BiometryInfo> {
  if (!isStoreBuild()) return { available: false, name: '' };
  try {
    const r = await BiometricAuth.checkBiometry();
    return { available: r.isAvailable, name: biometryName(r.biometryType) };
  } catch {
    return { available: false, name: '' };
  }
}

/**
 * Название биометрии для текста «…достаточно {{name}}».
 *
 * ⚠️ `biometryType` у плагина — ЧИСЛО (`touchId = 1`, `faceId = 2`, `fingerprintAuthentication = 3`,
 * `faceAuthentication = 4`). Прежде в окно шло `String(число)`: на iPhone с Face ID — «достаточно 2» (техдолг 237).
 * Face ID и Touch ID — названия Apple, их не переводят. Для Android и неизвестного значения — пусто: окно
 * берёт общий текст без названия (`biometrics.askDesc`).
 */
export function biometryName(type: unknown): string {
  switch (type) {
    case 1:
      return 'Touch ID';
    case 2:
      return 'Face ID';
    default:
      return '';
  }
}

export function biometricsEnabled(): boolean {
  try {
    return localStorage.getItem(PREF_KEY) === 'on';
  } catch {
    return false;
  }
}

export function setBiometricsEnabled(on: boolean): void {
  try {
    localStorage.setItem(PREF_KEY, on ? 'on' : 'off');
  } catch {
    /* приватный режим — настройка не переживёт перезапуск */
  }
}

/**
 * Просит подтвердить личность. Возвращает `true`, если подтвердил.
 *
 * ⚠️ Запасной путь обязателен: биометрия есть не у всех, палец может не
 * читаться, а при добавлении нового лица система намеренно обесценивает
 * доступ — это защита от подмены владельца. Отказ всегда должен вести к вводу
 * пароля, а не в тупик, иначе человек останется заперт снаружи собственного
 * аккаунта. Требование Apple тоже прямое: альтернатива обязана быть.
 */
export async function confirmIdentity(reason: string, fallbackTitle: string): Promise<boolean> {
  if (!isStoreBuild()) return true;
  try {
    await BiometricAuth.authenticate({
      reason,
      cancelTitle: fallbackTitle,
      allowDeviceCredential: true, // код-пароль устройства как запасной путь
      iosFallbackTitle: fallbackTitle,
      androidTitle: reason,
    });
    return true;
  } catch {
    return false;
  }
}

/**
 * Пора ли предложить включить вход по лицу.
 *
 * Спрашиваем ОДИН раз после первого входа и только там, где это работает:
 * в приложении из магазина и при наличии биометрии на устройстве. Отказ
 * запоминаем — переспрашивать то, от чего человек уже отказался, значит
 * приучать его закрывать наши окна не читая.
 */
export function shouldAskBiometrics(): boolean {
  if (!isStoreBuild()) return false;
  try {
    return localStorage.getItem(ASKED_KEY) !== 'yes';
  } catch {
    return false;
  }
}

export function markBiometricsAsked(): void {
  try {
    localStorage.setItem(ASKED_KEY, 'yes');
  } catch {
    /* приватный режим — спросим ещё раз, это не страшно */
  }
  const waiting = [...askedWaiters];
  askedWaiters.clear();
  waiting.forEach((done) => done());
}

const askedWaiters = new Set<() => void>();

/**
 * Дождаться, пока вопрос о входе по лицу будет отвечен (или окажется не нужен).
 *
 * Нужен тому, кто показывает СЛЕДУЮЩЕЕ окно после первого входа: системный вопрос об уведомлениях
 * (`lib/push.ts`) не должен лечь поверх нашего окна про Face ID. Вопрос уже отвечен или не задаётся вовсе
 * (сайт, приватный режим) — обещание готово сразу.
 */
export function whenBiometricsAsked(): Promise<void> {
  if (!shouldAskBiometrics()) return Promise.resolve();
  return new Promise((resolve) => {
    askedWaiters.add(resolve);
  });
}
